import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Col,
  Input,
  List,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { errorMessage } from '../../api';
import type { BillingApi, EmailTemplate } from '../../billingApi';
import { fillVariables, renderMarkdown } from '../../lib/markdown';

const { Text } = Typography;

interface Draft {
  subject: string;
  body: string;
  description: string;
}

const draftOf = (t: EmailTemplate): Draft => ({
  subject: t.subject,
  body: t.body,
  description: t.description,
});

const isDirty = (t: EmailTemplate, d: Draft) =>
  d.subject !== t.subject || d.body !== t.body || d.description !== t.description;

/** Edit the subject and text of every email, see it as the learner will, and send yourself a test. */
export const TemplatesTab: React.FC<{ billing: BillingApi }> = ({ billing }) => {
  const { message } = AntApp.useApp();
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // Edits are kept per template, so switching between templates loses nothing.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);

  useEffect(() => {
    let cancelled = false;
    billing
      .listTemplates()
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
        setSelected((s) => s ?? list[0]?.key ?? null);
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, reloadKey]);

  const template = useMemo(
    () => templates?.find((t) => t.key === selected) ?? null,
    [templates, selected]
  );
  const draft = template ? (drafts[template.key] ?? draftOf(template)) : null;
  const dirty = !!template && !!draft && isDirty(template, draft);

  const change = (patch: Partial<Draft>) => {
    if (!template || !draft) return;
    setDrafts((d) => ({ ...d, [template.key]: { ...draft, ...patch } }));
  };

  /** Saves the open draft; resolves true when the template on the server now matches it. */
  const save = async (): Promise<boolean> => {
    if (!template || !draft) return false;
    if (!dirty) return true;
    try {
      const saved = await billing.updateTemplate(template.key, draft);
      setTemplates((list) => list?.map((t) => (t.key === saved.key ? saved : t)) ?? null);
      setDrafts((d) => {
        const { [template.key]: _gone, ...rest } = d;
        return rest;
      });
      return true;
    } catch (e) {
      message.error(errorMessage(e));
      return false;
    }
  };

  const onSave = async () => {
    setBusy('save');
    if (await save()) message.success('Saved');
    setBusy(null);
  };

  const onTest = async () => {
    if (!template) return;
    setBusy('test');
    try {
      // The server sends what it has stored, so unsaved edits are saved first.
      if (await save()) {
        const to = await billing.testSend(template.key);
        message.success(`Test sent to ${to}`);
      }
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load the templates"
        description={loadError}
        action={<Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
      />
    );
  }
  if (!templates) return <Spin style={{ display: 'block', marginTop: 32 }} />;
  if (!templates.length) return <Text type="secondary">There are no email templates yet.</Text>;

  // Variables of the draft, with the example values the server suggested (anything new stays visible).
  const sample = template?.sample ?? {};

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={6}>
        <List
          bordered
          size="small"
          style={{ background: '#fff' }}
          dataSource={templates}
          renderItem={(t) => (
            <List.Item
              onClick={() => setSelected(t.key)}
              style={{
                cursor: 'pointer',
                background: t.key === selected ? '#eef0ff' : undefined,
                justifyContent: 'space-between',
              }}
            >
              <span>
                {t.key}
                {drafts[t.key] && isDirty(t, drafts[t.key]) && <Text type="warning"> •</Text>}
              </span>
              <Tag color={t.kind === 'service' ? 'blue' : 'orange'} style={{ marginInlineEnd: 0 }}>
                {t.kind === 'service' ? 'Service' : 'Reminder'}
              </Tag>
            </List.Item>
          )}
        />
      </Col>

      {template && draft && (
        <>
          <Col xs={24} lg={9}>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Input
                aria-label="Description"
                placeholder="Description"
                value={draft.description}
                maxLength={500}
                onChange={(e) => change({ description: e.target.value })}
              />
              <Input
                aria-label="Subject"
                placeholder="Subject"
                value={draft.subject}
                maxLength={200}
                onChange={(e) => change({ subject: e.target.value })}
              />
              <Input.TextArea
                aria-label="Text"
                rows={16}
                value={draft.body}
                maxLength={20000}
                onChange={(e) => change({ body: e.target.value })}
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13,
                }}
              />
              {template.variables.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {template.variables.map((v) => `{{${v}}}`).join('  ')}
                </Text>
              )}
              <Space>
                <Button
                  type="primary"
                  disabled={!dirty || !draft.subject.trim() || !draft.body.trim()}
                  loading={busy === 'save'}
                  onClick={() => void onSave()}
                >
                  Save
                </Button>
                <Button loading={busy === 'test'} onClick={() => void onTest()}>
                  Send me a test
                </Button>
              </Space>
            </Space>
          </Col>
          <Col xs={24} lg={9}>
            <Card
              size="small"
              title={fillVariables(draft.subject, sample) || ' '}
              aria-label="Preview"
            >
              {renderMarkdown(draft.body, sample)}
            </Card>
          </Col>
        </>
      )}
    </Row>
  );
};
